import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Upload, FileText, AlertCircle, ArrowLeft } from "lucide-react";
import { useMutation } from "convex/react";
import { api } from "@/convex/_generated/api";
import { toast } from "sonner";

interface BulkProductUploadProps {
  onCancel: () => void;
  onSuccess: () => void;
}

export default function BulkProductUpload({ onCancel, onSuccess }: BulkProductUploadProps) {
  const [file, setFile] = useState<File | null>(null);
  const [previewData, setPreviewData] = useState<any[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);

  const bulkCreateProducts = useMutation(api.products.bulkCreateProducts);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) {
      if (selectedFile.type !== "text/csv" && !selectedFile.name.endsWith(".csv")) {
        setError("Please upload a valid CSV file.");
        return;
      }
      setFile(selectedFile);
      setError(null);
      parseCSV(selectedFile);
    }
  };

  const parseCSV = (file: File) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const text = e.target?.result as string;
      try {
        const lines = text.split(/\r\n|\n/);
        const data = [];
        
        // Skip header if it looks like a header (contains "Composition" or "Brand")
        let startIndex = 0;
        if (lines[0] && (lines[0].toLowerCase().includes("composition") || lines[0].toLowerCase().includes("brand"))) {
          startIndex = 1;
        }

        for (let i = startIndex; i < lines.length; i++) {
          const line = lines[i].trim();
          if (!line) continue;
          
          // Simple CSV split (doesn't handle quoted commas, but sufficient for simple data)
          const columns = line.split(",").map(col => col.trim());
          
          // Expecting: Composition, Brand Name, Type, MRP
          // We need at least Composition, Brand Name, Type
          if (columns.length >= 3) {
            data.push({
              composition: columns[0] || "",
              brandName: columns[1] || "",
              type: columns[2] || "",
              mrp: columns[3] || "",
            });
          }
        }
        
        if (data.length === 0) {
          setError("No valid data found in CSV.");
        } else {
          setPreviewData(data);
        }
      } catch (err) {
        setError("Failed to parse CSV file.");
      }
    };
    reader.readAsText(file);
  };

  const handleUpload = async () => {
    if (previewData.length === 0) return;
    
    setIsUploading(true);
    try {
      await bulkCreateProducts({ products: previewData });
      toast.success(`Successfully added ${previewData.length} products.`);
      onSuccess();
    } catch (err) {
      console.error(err);
      toast.error("Failed to upload products. Please check the data and try again.");
      setError("An error occurred during upload.");
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
      <div className="flex items-center gap-4 mb-8">
        <Button variant="outline" size="icon" onClick={onCancel}>
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <h1 className="text-4xl font-bold">Bulk Upload Products</h1>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Upload CSV</CardTitle>
          <CardDescription>
            Upload a CSV file with the following columns: <strong>Composition, Brand Name, Type, MRP</strong>.
            <br />
            Note: Other fields will be set to default values and can be updated manually later.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="border-2 border-dashed rounded-lg p-8 text-center">
            <Input
              type="file"
              accept=".csv"
              className="hidden"
              id="csv-upload"
              onChange={handleFileChange}
            />
            <Label htmlFor="csv-upload" className="cursor-pointer flex flex-col items-center gap-2">
              <Upload className="h-10 w-10 text-muted-foreground" />
              <span className="text-lg font-medium">Click to upload CSV</span>
              <span className="text-sm text-muted-foreground">or drag and drop here</span>
            </Label>
            {file && (
              <div className="mt-4 flex items-center justify-center gap-2 text-green-600">
                <FileText className="h-4 w-4" />
                <span>{file.name}</span>
              </div>
            )}
          </div>

          {error && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertTitle>Error</AlertTitle>
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {previewData.length > 0 && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold">Preview ({previewData.length} products)</h3>
                <Button onClick={handleUpload} disabled={isUploading}>
                  {isUploading ? "Uploading..." : "Upload Products"}
                </Button>
              </div>
              <div className="border rounded-md overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Composition</TableHead>
                      <TableHead>Brand Name</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead>MRP</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {previewData.slice(0, 10).map((row, i) => (
                      <TableRow key={i}>
                        <TableCell>{row.composition}</TableCell>
                        <TableCell>{row.brandName}</TableCell>
                        <TableCell>{row.type}</TableCell>
                        <TableCell>{row.mrp}</TableCell>
                      </TableRow>
                    ))}
                    {previewData.length > 10 && (
                      <TableRow>
                        <TableCell colSpan={4} className="text-center text-muted-foreground">
                          ...and {previewData.length - 10} more
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
